import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Search, PenTool } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

type SitePlannerProject = {
  id: string;
  name: string;
  description: string | null;
  address: string | null;
  city: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  client_id: string | null;
  profiles: { display_name: string | null } | null;
  site_planner_scenarios: { id: string }[];
};

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  draft: { label: 'Concept', variant: 'secondary' },
  active: { label: 'Actief', variant: 'default' },
  archived: { label: 'Gearchiveerd', variant: 'outline' },
  sent_to_engineer: { label: 'Naar engineer', variant: 'default' },
};

export default function CRMTekenaar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<SitePlannerProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);

  const fetchProjects = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('site_planner_projects')
      .select('*, profiles!site_planner_projects_client_id_fkey(display_name), site_planner_scenarios(id)')
      .order('updated_at', { ascending: false }) as any;

    if (error) {
      console.error('Error fetching projects:', error);
      toast.error('Fout bij laden van terreinplannen');
    } else {
      setProjects(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchProjects(); }, []);

  const filtered = projects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.address?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleCreate = async () => {
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const { data: project, error } = await supabase
        .from('site_planner_projects')
        .insert({ name: newProject.name, description: newProject.description || null } as any)
        .select()
        .single();

      if (error) throw error;

      // Create first scenario
      await supabase
        .from('site_planner_scenarios')
        .insert({ project_id: (project as any).id, name: 'Scenario A' } as any);

      setCreateModalOpen(false);
      setNewProject({ name: '', description: '' });
      navigate(`/crm/tekenaar/${(project as any).id}`);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Fout bij aanmaken terreinplan');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Terreinplannen</h1>
          <p className="text-muted-foreground">Beheer en bewerk terreininrichtingen</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)} className="bg-primary hover:bg-primary/90">
          <Plus className="h-4 w-4 mr-2" />
          Nieuw Terreinplan
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek op naam, stad of adres..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="draft">Concept</SelectItem>
            <SelectItem value="active">Actief</SelectItem>
            <SelectItem value="archived">Gearchiveerd</SelectItem>
            <SelectItem value="sent_to_engineer">Naar engineer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Naam</TableHead>
              <TableHead>Klant</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Scenario's</TableHead>
              <TableHead>Laatste wijziging</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Laden...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Geen terreinplannen gevonden
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((project) => {
                const sc = statusConfig[project.status] || statusConfig.draft;
                return (
                  <TableRow
                    key={project.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/crm/tekenaar/${project.id}`)}
                  >
                    <TableCell className="font-medium">{project.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {project.profiles?.display_name || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={sc.variant}>{sc.label}</Badge>
                    </TableCell>
                    <TableCell>{project.site_planner_scenarios?.length || 0}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(project.updated_at), 'd MMM yyyy', { locale: nl })}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuw Terreinplan</DialogTitle>
            <DialogDescription>Maak een nieuw terreininrichtingsplan aan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Projectnaam *</Label>
              <Input
                id="name"
                value={newProject.name}
                onChange={(e) => setNewProject(p => ({ ...p, name: e.target.value }))}
                placeholder="bijv. Warehouse Eindhoven"
              />
            </div>
            <div>
              <Label htmlFor="description">Beschrijving</Label>
              <Textarea
                id="description"
                value={newProject.description}
                onChange={(e) => setNewProject(p => ({ ...p, description: e.target.value }))}
                placeholder="Optionele beschrijving..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Annuleren</Button>
            <Button onClick={handleCreate} disabled={!newProject.name.trim() || creating}>
              {creating ? 'Aanmaken...' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
